import { useEffect, useState } from "react";
import Lenis from "lenis";
import { useLocation } from "react-router-dom";
import "@/App.css";
import { PRODUCTS as STATIC_PRODUCTS } from "@/data/products";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductGrid } from "@/components/ProductGrid";
import { WhySection } from "@/components/WhySection";
import { Footer } from "@/components/Footer";
import { AuthCallback } from "@/components/AuthCallback";
import { Wishlist } from "@/components/Wishlist";
import { AdminPanel } from "@/components/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Landing() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const [favorites, setFavorites] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const refreshProducts = () => {
    fetch(`${BACKEND_URL}/api/products`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          setProducts(data.map((p) => ({ ...p, id: p.product_id })));
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (location.state?.user) return;
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => setUser(u))
      .catch(() => setUser(null));
  }, [location.state]);

  useEffect(() => {
    refreshProducts();
  }, []);

  useEffect(() => {
    if (!user) {
      setFavorites([]);
      return;
    }
    fetch(`${BACKEND_URL}/api/favorites`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { product_ids: [] }))
      .then((d) => setFavorites(d.product_ids || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = () => {
    fetch(`${BACKEND_URL}/api/auth/logout`, { method: "POST", credentials: "include" })
      .finally(() => {
        setUser(null);
        setShowAdmin(false);
      });
  };

  const toggleFavorite = (product) => {
    if (!user) {
      handleSignIn();
      return;
    }
    const isFav = favorites.includes(product.id);
    setFavorites((prev) => (isFav ? prev.filter((id) => id !== product.id) : [...prev, product.id]));
    fetch(`${BACKEND_URL}/api/favorites${isFav ? `/${product.id}` : ""}`, {
      method: isFav ? "DELETE" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: isFav ? undefined : JSON.stringify({ product_id: product.id }),
    }).catch(() => {});
  };

  return (
    <div className="App grain bg-[#0B0C0E] text-slate-50">
      <Navbar
        user={user}
        onLogout={handleLogout}
        onSignIn={handleSignIn}
        favoritesCount={favorites.length}
        onOpenWishlist={() => setShowWishlist(true)}
        onOpenAdmin={() => setShowAdmin(true)}
      />
      <main>
        <Hero />
        <Marquee />
        <ProductGrid products={products} favorites={favorites} onToggleFavorite={toggleFavorite} />
        <WhySection />
      </main>
      <Footer />
      {showWishlist && (
        <Wishlist
          products={products}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onClose={() => setShowWishlist(false)}
        />
      )}
      {showAdmin && user?.is_admin && (
        <AdminPanel
          products={products}
          onClose={() => setShowAdmin(false)}
          onChanged={refreshProducts}
        />
      )}
    </div>
  );
}

function App() {
  const location = useLocation();
  // Detect OAuth return synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <Landing />;
}

export default App;
